import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB (pre-compression limit)
const MAX_DIMENSION = 1920; // Max width/height in pixels
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Validate image file before upload
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateImageFile(file) {
  if (!file) {
    return { valid: false, error: 'Ingen fil valt' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Ugyldig filtype. Bruk JPG, PNG, GIF eller WebP.' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'Fila er for stor. Maks 15MB.' };
  }

  return { valid: true };
}

/**
 * Compress and convert an image to WebP format using Canvas API.
 * Scales down images larger than MAX_DIMENSION while preserving aspect ratio.
 * @param {File} file - Original image file
 * @param {number} quality - WebP quality 0-1 (default 0.82)
 * @returns {Promise<File>} Compressed WebP file
 */
export function compressToWebP(file, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Calculate dimensions (scale down if needed)
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            return reject(new Error('Klarte ikkje konvertere biletet'));
          }
          const webpFile = new File(
            [blob],
            file.name.replace(/\.[^.]+$/, '.webp'),
            { type: 'image/webp' }
          );
          resolve(webpFile);
        },
        'image/webp',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Klarte ikkje laste biletet'));
    };

    img.src = objectUrl;
  });
}

/**
 * Upload an image for a quiz question.
 * Automatically compresses to WebP before uploading.
 * @param {File} file - The image file to upload
 * @param {string} quizId - The quiz ID
 * @param {number} questionIndex - The question index (0-based)
 * @returns {Promise<string>} - The download URL
 */
export async function uploadQuizImage(file, quizId, questionIndex) {
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Compress to WebP before upload
  const compressed = await compressToWebP(file);

  // Always save as .webp
  const imagePath = `quizzes/${quizId}/question-${questionIndex}.webp`;
  const imageRef = ref(storage, imagePath);

  // Upload compressed file
  await uploadBytes(imageRef, compressed);

  // Get and return download URL
  const downloadURL = await getDownloadURL(imageRef);
  return downloadURL;
}

/**
 * Delete a quiz image from storage
 * @param {string} imageUrl - The full download URL of the image
 * @returns {Promise<void>}
 */
export async function deleteQuizImage(imageUrl) {
  if (!imageUrl) return;

  try {
    // Extract the path from the URL
    const url = new URL(imageUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
    if (pathMatch) {
      const imagePath = decodeURIComponent(pathMatch[1]);
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
    }
  } catch (error) {
    console.error('Error deleting image:', error);
    // Don't throw - deletion failure shouldn't break the flow
  }
}

/**
 * Upload multiple images for a quiz (one per question)
 * @param {Array<File|null>} files - Array of files (null for questions without images)
 * @param {string} quizId - The quiz ID
 * @returns {Promise<Array<string|null>>} - Array of download URLs (null for questions without images)
 */
export async function uploadQuizImages(files, quizId) {
  const uploadPromises = files.map(async (file, index) => {
    if (!file) return null;
    return uploadQuizImage(file, quizId, index);
  });

  return Promise.all(uploadPromises);
}
